import { useState, useEffect, useCallback } from "react";
import HeaderFilter from "../../views/dashboard/components/HeaderFilter";
import KanbanSearch from "./KanbanSearch";
import styles from "./Kanban.module.scss";

import { apiCall } from "../../store/services/service";
import { BASE_URL } from "../../store/services/URL";
import { AddIconSvg } from "../../modules/default-svg";
import Button from "../controls/button/Button";
import { getCreatePermission } from "../../helpers/utils";

/**
 * Reusable header with Project + Scope filters (search-backed).
 *
 * @param {Object}   props
 * @param {function} props.onProjectChange  - (id, name) called when user picks a project (id=null for "all")
 * @param {function} props.onScopeChange    - (id, name) called when user picks a scope   (id=null for "all")
 */
const KanbanHeader = (props) => {

  const { onProjectChange, onScopeChange, onSearch, onCreateTask } = props;

  // ─── Task search state ────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  // ─── Project filter state ─────────────────────────────────────
  const [ProjectId, setProjectId] = useState("");
  const [ProjectName, setProjectName] = useState("All Projects");
  const [allFilterProjects, setAllFilterProjects] = useState([]);
  const [filterValue, setFilterValue] = useState("");
  const [filterSearchTerm, setFilterSearchTerm] = useState("");

  // ─── Scope filter state ───────────────────────────────────────
  const [ScopeId, setScopeId] = useState("");
  const [ScopeName, setScopeName] = useState("All Scopes");
  const [allFilterScopes, setAllFilterScopes] = useState([]);
  const [scopeFilterValue, setScopeFilterValue] = useState("");
  const [scopeFilterSearchTerm, setScopeFilterSearchTerm] = useState("");

  // ─── Popover state ────────────────────────────────────────────
  const [isFilterOpenPopover, setIsFilterOpenPopover] = useState("");

  // ─── Search input handlers ────────────────────────────────────
  const handleChange = (value) => {
    setFilterValue(value);
    setFilterSearchTerm(value);
  };

  const handleScopeChange = (value) => {
    setScopeFilterValue(value);
    setScopeFilterSearchTerm(value);
  };

  // ─── Project selected → reset scope ───────────────────────────
  const handleProjectSelected = (id) => {
    setProjectId(id);
    setScopeFilterValue(null);
    setScopeName("All Scopes");
    setScopeId("");
    setAllFilterScopes([]);
  };

  // ─── Fetch search data (mirrors DashboardHeader.fetchFilterData) ──
  const fetchFilterData = useCallback(
    (entityType) => {
      if (!entityType) return;

      let url = `${BASE_URL}search/${entityType}?`;

      if (entityType === "scopes") {
        url += ProjectId ? `projectId=${ProjectId}&` : "";
        url += scopeFilterSearchTerm
          ? `searchTerm=${scopeFilterSearchTerm}`
          : "";
      } else {
        url += `searchTerm=${filterSearchTerm}&`;
      }

      return apiCall
        .get({ url, isAuthToken: true })
        .then((res) => {
          switch (entityType) {
            case "projects":
              setAllFilterProjects(res);
              break;
            case "scopes":
              setAllFilterScopes(res);
              break;
            default:
              break;
          }
          return res;
        })
        .catch((error) => {
          console.error(`Error fetching ${entityType} data:`, error);
        });
    },
    [ProjectId, filterSearchTerm, scopeFilterSearchTerm]
  );

  // ─── Trigger fetch when popover opens / search term changes ───
  useEffect(() => {
    if (isFilterOpenPopover) {

      fetchFilterData(isFilterOpenPopover);
    }
  }, [filterSearchTerm, scopeFilterSearchTerm, isFilterOpenPopover, fetchFilterData]);

  const handleFilterPopoverToggle = (type) => {
    if(type === "projects"){
      setFilterSearchTerm("");
    }else {
      setScopeFilterSearchTerm("");
    }
    setIsFilterOpenPopover(`${type}`);
  };

  // ─── Callback to parent on project change ─────────────────────
  useEffect(() => {
    if (onProjectChange) {
      onProjectChange(ProjectId || null, ProjectName);
    }
  }, [ProjectId, ProjectName]);

  // ─── Callback to parent on scope change ───────────────────────
  useEffect(() => {
    if (onScopeChange) {
      onScopeChange(ScopeId || null, ScopeName);
    }
  }, [ScopeId, ScopeName]);

  // ─── No-ops for props HeaderFilter expects but we don't use ───
  const noop = () => {};

  return (
    <div className={styles.DbHeader}>
      <div className="d-flex align-items-center gap-3">
      <KanbanSearch
          searchTerm={searchTerm}
          handleChange={setSearchTerm}
          triggerSearch={onSearch}
        />
        <HeaderFilter
          itemType="projects"
          selectedItemId={ProjectId}
          selectedItemName={ProjectId ? ProjectName : "All Projects"}
          allItems={allFilterProjects}
          filterValue={filterValue}
          handleChange={handleChange}
          openPopover={isFilterOpenPopover}
          setIsOpenPopover={setIsFilterOpenPopover}
          setSelectedItemId={handleProjectSelected}
          setSelectedItemName={setProjectName}
          setBaselines={noop}
          setFilterBaseLine={noop}
          styles={styles}
          handleFilterPopoverToggle={handleFilterPopoverToggle}
          setFilterSearchTerm={setFilterSearchTerm}
        />
        <HeaderFilter
          itemType="scopes"
          selectedItemId={ScopeId}
          selectedItemName={ScopeId ? ScopeName : "All Scopes"}
          allItems={allFilterScopes}
          filterValue={scopeFilterValue}
          handleChange={handleScopeChange}
          openPopover={isFilterOpenPopover}
          setIsOpenPopover={setIsFilterOpenPopover}
          setSelectedItemId={setScopeId}
          setSelectedItemName={setScopeName}
          setBaselines={noop}
          setFilterBaseLine={noop}
          styles={styles}
          handleFilterPopoverToggle={handleFilterPopoverToggle}
          setFilterSearchTerm={setScopeFilterValue}
        />
      </div>
      {!!getCreatePermission("task") && (
        <Button
          btnClass="d_btn d_btn_primary ms-12"
          icon={<AddIconSvg />}
          label={"Add Task"}
          clickHandler={() => onCreateTask(true)}
        />
      )}
    </div>
  );
};

export default KanbanHeader;
